/**
 * Factura: borrador, emisión y rectificación.
 *
 * **Una factura no se edita.** Se prepara como borrador, se emite —y en
 * ese momento entra en la cadena de registro Veri\*Factu, encadenada con
 * la anterior— y si está mal se **rectifica** con otra factura que la
 * corrige. Ésa no es una limitación técnica: es el art. 15 del RD
 * 1619/2012, y una interfaz que ofreciera «editar» estaría enseñando algo
 * que no existe.
 *
 * Por eso esta pantalla tiene dos caras según el estado:
 *
 * - **BORRADOR** — se puede cambiar todo, y hay un botón de emitir con su
 *   aviso de irreversibilidad.
 * - **EMITIDA** — sólo se lee. Las acciones son anular (si aún no ha
 *   surtido efecto) o rectificar.
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSession } from '../../session/session-context.js';
import { ApiError } from '../../session/api-error.js';
import { useForm } from '../../forms/use-form.js';
import { FormSection, TextArea, TextField } from '../../forms/fields.js';
import { EntityPicker } from '../../forms/entity-picker.js';
import {
  LineEditor,
  TotalsSummary,
  emptyLine,
  useLineTotals,
  type EditableLine,
} from '../../forms/line-editor.js';
import { isoDate, type FieldRules } from '../../forms/form-state.js';
import { money } from '../../table/format.js';
import { PdfButton } from '../pdf-button.js';

interface RegistryInfo {
  /** Huella del registro: identifica la versión de la que sale el PDF. */
  huella: string | null;
}

interface InvoiceDetail {
  id: string;
  number: string;
  status: string;
  invoice_type: string;
  issue_date: string;
  due_date: string | null;
  customer_id: string;
  recipient_name: string | null;
  taxable_base: string;
  tax_total: string;
  total: string;
  currency: string;
}

const RULES: FieldRules = {
  issueDate: [isoDate()],
  dueDate: [isoDate()],
  operationDate: [isoDate()],
};

export function InvoiceFormScreen(): React.ReactNode {
  const { api } = useSession();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const existing = id !== undefined && id !== 'nueva';

  const counter = useRef(1);
  const nextId = (): string => `l${counter.current++}`;

  const [detail, setDetail] = useState<InvoiceDetail | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [huella, setHuella] = useState<string | null>(null);

  const [customerId, setCustomerId] = useState('');
  const [customerError, setCustomerError] = useState<string | undefined>(undefined);
  const [lines, setLines] = useState<EditableLine[]>([emptyLine(nextId())]);
  const [lineError, setLineError] = useState<string | null>(null);

  useEffect(() => {
    if (!existing) return;
    let cancelled = false;

    api
      .get<InvoiceDetail>(`/invoices/${id}`)
      .then(async (data) => {
        if (cancelled) return;
        setDetail(data);

        // La huella sólo existe si la factura está emitida, y es lo que
        // identifica QUÉ versión se renderiza.
        if (data.status !== 'BORRADOR') {
          const registry = await api
            .get<RegistryInfo>(`/invoices/${id}/registry`)
            .catch(() => ({ huella: null }));
          if (!cancelled) setHuella(registry.huella);
        }
      })
      .catch((caught: unknown) => {
        if (!cancelled) {
          setLoadError(caught instanceof ApiError ? caught.message : 'No se ha podido cargar la factura.');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [api, existing, id]);

  const form = useForm<{ id: string; number: string }>({
    mode: 'create',
    rules: RULES,
    nullable: ['dueDate', 'operationDate', 'contractId'],
    initial: {
      issueDate: new Date().toISOString().slice(0, 10),
      operationDate: '',
      dueDate: '',
      paymentTerms: '',
      notes: '',
    },
    onSubmit: async (body, client) =>
      client.post<{ id: string; number: string }>('/invoices', {
        ...body,
        customerId,
        lines: lines
          .filter((line) => line.description.trim() !== '')
          .map((line) => ({
            kind: line.kind,
            description: line.description.trim(),
            quantity: line.kind === 'TEXTO' ? '0' : line.quantity.replace(',', '.'),
            unitPrice: line.kind === 'TEXTO' ? '0.00' : line.unitPrice.replace(',', '.'),
            discountRate: line.discountRate.trim() === '' ? null : line.discountRate.replace(',', '.'),
            taxRate: line.kind === 'TEXTO' ? '0' : line.taxRate,
          })),
      }),
    onSaved: (saved) => void navigate(`/facturas/${saved.id}`),
  });

  const totals = useLineTotals(lines, '');

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();

    if (customerId === '') {
      setCustomerError('Elija el cliente.');
      return;
    }
    setCustomerError(undefined);

    if (lines.filter((line) => line.description.trim() !== '').length === 0) {
      setLineError('Una factura necesita al menos una línea con concepto.');
      return;
    }
    if (totals.invalidLines.length > 0) {
      setLineError('Hay líneas con números que no se pueden interpretar.');
      return;
    }
    setLineError(null);

    await form.submit();
  }

  async function issue(): Promise<void> {
    if (!detail) return;

    // El aviso dice exactamente lo que pasa. «¿Está seguro?» no informa de
    // nada; «entra en la cadena de registro y ya no se puede modificar» sí.
    const confirmed = window.confirm(
      `Se va a emitir la factura ${detail.number} por ${detail.total} ${detail.currency}.\n\n` +
        'Al emitirla entra en la cadena de registro y YA NO SE PUEDE MODIFICAR. ' +
        'Si estuviera mal, habría que rectificarla con otra factura.\n\n¿Emitir?',
    );
    if (!confirmed) return;

    setBusy(true);
    setActionError(null);
    try {
      await api.post(`/invoices/${detail.id}/issue`);
      setDetail(await api.get<InvoiceDetail>(`/invoices/${detail.id}`));
    } catch (caught) {
      setActionError(caught instanceof ApiError ? caught.message : 'No se ha podido emitir.');
    } finally {
      setBusy(false);
    }
  }

  if (loadError !== null) {
    return (
      <section className="screen">
        <p className="screen__error" role="alert">
          {loadError}
        </p>
      </section>
    );
  }

  // ── Factura ya existente ───────────────────────────────────────────

  if (existing) {
    if (!detail) {
      return (
        <section className="screen">
          <p className="table__loading">Cargando la factura…</p>
        </section>
      );
    }

    const draft = detail.status === 'BORRADOR';

    return (
      <section className="screen">
        <header className="screen__head">
          <h1 className="screen__title">Factura {detail.number}</h1>
          <span className={`pill pill--${draft ? 'off' : detail.status === 'ANULADA' ? 'bad' : 'ok'}`}>
            {draft ? '• Borrador' : detail.status === 'ANULADA' ? '✕ Anulada' : '✓ Emitida'}
          </span>
        </header>

        {actionError !== null && (
          <p className="screen__error" role="alert">
            {actionError}
          </p>
        )}

        <div className="cards">
          <div className="card">
            <p className="card__label">Cliente</p>
            <p className="card__value">{detail.recipient_name ?? '—'}</p>
          </div>
          <div className="card">
            <p className="card__label">Base imponible</p>
            <p className="card__value">{money(detail.taxable_base, detail.currency)}</p>
          </div>
          <div className="card">
            <p className="card__label">IVA</p>
            <p className="card__value">{money(detail.tax_total, detail.currency)}</p>
          </div>
          <div className="card card--alert">
            <p className="card__label">Total</p>
            <p className="card__value">{money(detail.total, detail.currency)}</p>
          </div>
        </div>

        {draft ? (
          <>
            <p className="screen__note screen__note--strong">
              Todavía es un borrador: no tiene efectos y se puede corregir. Al emitirla entrará en la
              cadena de registro y <strong>ya no se podrá modificar</strong>.
            </p>
            <div className="form__actions">
              <button className="button button--primary" type="button" disabled={busy} onClick={() => void issue()}>
                {busy ? 'Emitiendo…' : 'Emitir factura'}
              </button>
            </div>
          </>
        ) : (
          <p className="screen__note">
            Una factura emitida <strong>no se edita</strong> (art. 15 RD 1619/2012). Si está mal, se
            rectifica con otra factura que la corrige, y las dos quedan en el registro.
          </p>
        )}

        <div className="form__actions">
          <a
            className="button button--primary button--small"
            href={`/v1/invoices/${detail.id}/print`}
            target="_blank"
            rel="noreferrer"
          >
            {draft ? 'Ver el borrador' : 'Ver e imprimir la factura'}
          </a>
          {!draft && huella !== null && (
            <PdfButton kind="INVOICE" entityId={detail.id} sourceHash={huella} label="Generar el PDF" />
          )}
          <button className="button button--quiet" type="button" onClick={() => void navigate('/facturas')}>
            Volver al listado
          </button>
        </div>
      </section>
    );
  }

  // ── Borrador nuevo ─────────────────────────────────────────────────

  return (
    <section className="screen">
      <header className="screen__head">
        <h1 className="screen__title">Nueva factura</h1>
        {form.dirty && <span className="screen__count">Cambios sin guardar</span>}
      </header>

      <p className="screen__note">
        Se crea como <strong>borrador</strong>. La emisión es un paso aparte y es el que tiene
        efectos: hasta entonces no consume número de la serie fiscal ni entra en la cadena de
        registro.
      </p>

      {(form.formError !== null || lineError !== null) && (
        <p className="screen__error" role="alert">
          {lineError ?? form.formError}
        </p>
      )}

      {form.orphanErrors.length > 0 && (
        <p className="screen__error" role="alert">
          El servidor ha rechazado campos que este formulario no muestra:{' '}
          {form.orphanErrors.join(' · ')}
        </p>
      )}

      <form className="form" onSubmit={(e) => void submit(e)} noValidate>
        <FormSection title="Destinatario y fechas">
          <EntityPicker
            label="Cliente"
            entity="CUSTOMER"
            value={customerId}
            onChange={(v) => {
              setCustomerId(v);
              setCustomerError(undefined);
            }}
            error={customerError}
            required
          />
          <TextField
            label="Fecha de emisión"
            type="date"
            value={String(form.values['issueDate'] ?? '')}
            onChange={(v) => form.set('issueDate', v)}
            onBlur={() => form.blur('issueDate')}
            error={form.errors['issueDate']}
            required
          />
          <TextField
            label="Fecha de operación"
            type="date"
            value={String(form.values['operationDate'] ?? '')}
            onChange={(v) => form.set('operationDate', v)}
            onBlur={() => form.blur('operationDate')}
            error={form.errors['operationDate']}
            help="Cuando se prestó el servicio, si es distinta de la de emisión (art. 6.1.f)."
          />
          <TextField
            label="Vencimiento"
            type="date"
            value={String(form.values['dueDate'] ?? '')}
            onChange={(v) => form.set('dueDate', v)}
            onBlur={() => form.blur('dueDate')}
            error={form.errors['dueDate']}
            help="La Ley 3/2004 fija 30 días por defecto y 60 como máximo pactable entre empresas."
          />
          <TextField
            label="Condiciones de pago"
            value={String(form.values['paymentTerms'] ?? '')}
            onChange={(v) => form.set('paymentTerms', v)}
          />
        </FormSection>

        <FormSection title="Líneas">
          <div className="form__full">
            <LineEditor lines={lines} onChange={setLines} totals={totals} nextId={nextId} />
          </div>
          <div className="form__full">
            <TotalsSummary totals={totals} />
          </div>
        </FormSection>

        <FormSection title="Otros">
          <TextArea
            label="Notas"
            value={String(form.values['notes'] ?? '')}
            onChange={(v) => form.set('notes', v)}
          />
        </FormSection>

        <footer className="form__actions">
          <button className="button button--primary" type="submit" disabled={form.saving}>
            {form.saving ? 'Creando…' : 'Crear borrador'}
          </button>
          <button
            className="button button--quiet"
            type="button"
            onClick={() => {
              if (form.dirty && !window.confirm('Hay cambios sin guardar. ¿Salir igualmente?')) return;
              void navigate('/facturas');
            }}
          >
            Volver
          </button>
        </footer>
      </form>
    </section>
  );
}
